import { useEffect, useState, useCallback } from "react";
import { useDriver } from "../context/DriverContext";
import axios from "axios";
import { CheckCircle, XCircle } from "lucide-react"; // icons for delivered/failed

export default function DriverHistory() {
  const { driver } = useDriver();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const fetchHistory = useCallback(async () => {
    if (!driver) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/driver/deliveries/history`, {
        withCredentials: true,
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.deliveries || [];
      setHistory(data);
    } catch (err) {
      console.error("Failed to fetch delivery history:", err);
      setError(err.response?.data?.message || "Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [driver, BASE_URL]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (!driver) return null;

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6 mt-4">Delivery History</h1>

      {error && (
        <div className="bg-red-100 text-red-700 text-sm px-3 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Loading delivery history...</p>
      ) : history.length === 0 ? (
        <p className="text-gray-500 text-center">No deliveries completed yet.</p>
      ) : (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-1 lg:grid-cols-3">
          {history.map((delivery) => (
            <div
              key={delivery._id}
              className="bg-white shadow-md rounded-xl p-4 border border-gray-200"
            >
              {/* Header with status icon on top and order number below */}
              <div className="flex flex-col items-start mb-3">
                {/* Status & icon */}
                <div className="flex items-center gap-2">
                  {delivery.status === "Delivered" && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  {delivery.status === "Failed Delivery" && (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span
                    className={`font-medium ${
                      delivery.status === "Delivered" ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {delivery.status}
                  </span>
                </div>

                {/* Order number below */}
                <h2 className="text-lg font-semibold truncate mt-1">
                  #{delivery.orderNumber}
                </h2>
              </div>

              {/* Customer Info */}
              <div className="space-y-1 text-sm text-gray-700">
                <p><strong>Customer Name:</strong> {delivery.customerName}</p>
                <p><strong>Customer Email:</strong> {delivery.customerEmail}</p>
                <p><strong>Phone:</strong> {delivery.customerPhone}</p>
                <p><strong>Address:</strong> {delivery.address}</p>
                <p><strong>City:</strong> {delivery.deliveryCity}</p>
                <p>
                  <strong>
                    {delivery.status === "Delivered" ? "Delivered At:" : "Failed At:"}
                  </strong>{" "}
                  {new Date(delivery.deliveredAt).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
